import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "react-query";
import {
  listNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "api/notification";
import {
  isAlreadyFollowingMessage,
  removeFollowRequest,
  sendFollowRequest,
  syncConnectionRequestFollow,
} from "Components/PeopleList/Api";
import { baseURL, getToken } from "Shared/baseURL";
import { useNotify, useReduxUser, useReduxUserProfile } from "Utils/hooks";
import { useConnectionRequests, useFollowers } from "Utils/queries";
import { adaptNotification } from "./notificationAdapter";
import {
  MARK_ALL_FEED_READ,
  MARK_FEED_NOTIFICATION_READ,
} from "Redux/ActionTypes";

export const NOTIFICATION_TABS = [
  { label: "All", value: "all" },
  { label: "Unread", value: "unread" },
  { label: "Requests", value: "request" },
];

const USER_ID_FIELDS = [
  "id",
  "userId",
  "user_id",
  "uuid",
  "userUuid",
  "user_uuid",
  "publicId",
  "public_id",
  "sub",
  "username",
  "userName",
  "user_name",
  "preferredUsername",
  "preferred_username",
  "email",
];
const RECIPIENT_FIELDS = [
  "recipient",
  "recipients",
  "recipientUser",
  "recipientUsers",
  "receiver",
  "receivers",
  "receiverUser",
  "receiverUsers",
  "targetUser",
  "targetUsers",
  "toUser",
  "toUsers",
];
const RECIPIENT_ID_FIELDS = [
  "recipientId",
  "recipientIds",
  "recipientUserId",
  "recipientUserIds",
  "receiverId",
  "receiverIds",
  "receiverUserId",
  "receiverUserIds",
  "targetUserId",
  "targetUserIds",
  "toUserId",
  "toUserIds",
];

const NOTIFICATION_HISTORY_STORAGE_PREFIX = "kewordal:notifications:history:";
const NOTIFICATION_RETENTION_DAYS = 30;
const NOTIFICATION_RETENTION_MS =
  NOTIFICATION_RETENTION_DAYS * 24 * 60 * 60 * 1000;

const getNotificationIdentity = (notification) =>
  normalizeId(notification?.id ?? notification?.messageId);

const sortNotificationsByCreatedAt = (notifications = []) =>
  [...notifications].sort(
    (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0),
  );

const mergeNotificationLists = (...lists) => {
  const byId = new Map();

  lists.flat().forEach((notification) => {
    if (!notification || typeof notification !== "object") return;
    const id = getNotificationIdentity(notification);
    if (!id) return;

    const existing = byId.get(id);
    const merged = {
      ...(existing || {}),
      ...notification,
      id,
    };

    if (existing?.isRead || notification?.isRead) {
      merged.isRead = true;
      merged.readAt =
        notification?.readAt || existing?.readAt || new Date().toISOString();
    }

    byId.set(id, merged);
  });

  return sortNotificationsByCreatedAt([...byId.values()]);
};

const readNotificationHistory = (storageKey) => {
  if (!storageKey || typeof window === "undefined") return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeNotificationHistory = (storageKey, notifications) => {
  if (!storageKey || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify(sortNotificationsByCreatedAt(notifications)),
    );
  } catch {
    // Keep the in-memory list even if browser storage is unavailable/full.
  }
};

const normalizeId = (value) => {
  if (value == null) return "";
  const normalized = String(value).trim();
  return normalized || "";
};

const collectIds = (source, fields) => {
  if (!source || typeof source !== "object") return [];

  return fields.map((field) => normalizeId(source[field])).filter(Boolean);
};

const collectEntityIds = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap(collectEntityIds);
  }

  if (value && typeof value === "object") {
    return collectIds(value, USER_ID_FIELDS);
  }

  const id = normalizeId(value);
  return id ? [id] : [];
};

const collectFieldEntityIds = (source, fields) => {
  if (!source || typeof source !== "object") return [];

  return fields.flatMap((field) => collectEntityIds(source[field]));
};

const collectCurrentUserIds = (...sources) => [
  ...new Set(sources.flatMap((source) => collectIds(source, USER_ID_FIELDS))),
];

const collectNotificationRecipientIds = (notification) => {
  if (!notification || typeof notification !== "object") return [];

  const nestedRecipients = collectFieldEntityIds(notification, RECIPIENT_FIELDS);
  const directIds = collectFieldEntityIds(notification, RECIPIENT_ID_FIELDS);
  const metadataIds = collectFieldEntityIds(
    notification.metadata,
    RECIPIENT_ID_FIELDS,
  );
  const dataIds = collectFieldEntityIds(notification.data, RECIPIENT_ID_FIELDS);

  return [
    ...new Set([...nestedRecipients, ...directIds, ...metadataIds, ...dataIds]),
  ];
};

const belongsToCurrentUser = (notification, currentUserIds) => {
  const recipientIds = collectNotificationRecipientIds(notification);
  if (!recipientIds.length) return true;
  if (!currentUserIds.length) return true;

  const currentIds = new Set(currentUserIds);
  return recipientIds.some((id) => currentIds.has(id));
};

const isExpiredNotification = (notification) => {
  if (notification?.isActionRequired) return false;

  const createdAt = new Date(notification?.createdAt || 0).getTime();
  if (!createdAt) return false;

  return Date.now() - createdAt > NOTIFICATION_RETENTION_MS;
};

const getActionErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData.trim();
  }

  return (
    responseData?.message ||
    responseData?.error_description ||
    responseData?.error ||
    "Couldn't complete that notification action."
  );
};

export function useNotifications() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const profile = useReduxUserProfile();
  const user = useReduxUser();
  const tokenParsed = useSelector((state) => state.auth?.keycloak?.tokenParsed);
  const userIdCandidates = [
    profile?.id ??
      profile?.userId ??
      user?.id ??
      user?.userId ??
      user?.user_id ??
      tokenParsed?.userId ??
      tokenParsed?.user_id ??
      tokenParsed?.sub ??
      null,
    profile?.userId,
    profile?.user_id,
    profile?.id,
    user?.userId,
    user?.user_id,
    user?.id,
    tokenParsed?.userId,
    tokenParsed?.user_id,
    tokenParsed?.sub,
  ];
  const notificationRecipientId =
    userIdCandidates.map(normalizeId).find(Boolean) || "";
  const currentUserIdsKey = collectCurrentUserIds(profile, user, tokenParsed).join(
    "|",
  );
  const currentUserIds = useMemo(
    () => currentUserIdsKey.split("|").filter(Boolean),
    [currentUserIdsKey],
  );

  const notificationFeed = useSelector(
    (state) => state.notificationFeed?.notifications ?? [],
  );

  const [apiNotifications, setApiNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const hasCompletedInitialFetch = useRef(false);
  const notificationHistoryStorageKey = notificationRecipientId
    ? `${NOTIFICATION_HISTORY_STORAGE_PREFIX}${notificationRecipientId}`
    : "";
  const [archivedNotifications, setArchivedNotifications] = useState([]);
  const { data: receivedConnectionRequestsData, refetch: refetchRequests } =
    useConnectionRequests(notificationRecipientId, "RECEIVED", "PENDING", {
      enabled: !!notificationRecipientId,
    });
  const { data: followingData, refetch: refetchFollowing } = useFollowers(
    notificationRecipientId,
    "FOLLOWEES",
    {
      enabled: !!notificationRecipientId,
    },
  );

  useEffect(() => {
    setArchivedNotifications(
      readNotificationHistory(notificationHistoryStorageKey),
    );
  }, [notificationHistoryStorageKey]);

  const archiveNotifications = useCallback(
    (nextNotifications = []) => {
      if (!notificationHistoryStorageKey || !nextNotifications.length) return;

      setArchivedNotifications((prev) => {
        const merged = mergeNotificationLists(prev, nextNotifications);
        writeNotificationHistory(notificationHistoryStorageKey, merged);
        return merged;
      });
    },
    [notificationHistoryStorageKey],
  );

  const updateArchivedNotifications = useCallback(
    (updater) => {
      setArchivedNotifications((prev) => {
        const next = sortNotificationsByCreatedAt(updater(prev));
        writeNotificationHistory(notificationHistoryStorageKey, next);
        return next;
      });
    },
    [notificationHistoryStorageKey],
  );

  const fetchFromApi = useCallback(async () => {
    if (!notificationRecipientId) {
      setApiNotifications([]);
      setIsLoading(false);
      return;
    }

    const isInitialFetch = !hasCompletedInitialFetch.current;
    if (isInitialFetch) {
      setIsLoading(true);
      setError(null);
    }

    try {
      const { notifications, pagination } = await listNotifications({
        page: 0,
        size: 50,
        recipient: notificationRecipientId,
      });
      const currentUserNotifications = notifications.filter((notification) =>
        belongsToCurrentUser(notification, currentUserIds),
      );

      setError(null);
      setApiNotifications(currentUserNotifications);
      archiveNotifications(currentUserNotifications);
      setCurrentPage(0);
      setHasNext(pagination.hasNext);
    } catch (err) {
      if (isInitialFetch) {
        setError(err);
      }
    } finally {
      hasCompletedInitialFetch.current = true;
      if (isInitialFetch) {
        setIsLoading(false);
      }
    }
  }, [archiveNotifications, currentUserIds, notificationRecipientId]);

  useEffect(() => {
    hasCompletedInitialFetch.current = false;
    if (notificationRecipientId) {
      fetchFromApi();
    } else {
      setApiNotifications([]);
      setIsLoading(false);
    }
  }, [notificationRecipientId, fetchFromApi]);

  useEffect(() => {
    if (!notificationRecipientId) return;

    const refreshOnFocus = () => {
      if (document.visibilityState === "visible") {
        fetchFromApi();
      }
    };
    window.addEventListener("focus", fetchFromApi);
    window.addEventListener("kewordal:notification-refresh", fetchFromApi);
    document.addEventListener("visibilitychange", refreshOnFocus);

    return () => {
      window.removeEventListener("focus", fetchFromApi);
      window.removeEventListener("kewordal:notification-refresh", fetchFromApi);
      document.removeEventListener("visibilitychange", refreshOnFocus);
    };
  }, [fetchFromApi, notificationRecipientId]);

  const loadMore = useCallback(async () => {
    if (!hasNext || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const { notifications: more, pagination } = await listNotifications({
        page: nextPage,
        size: 20,
        recipient: notificationRecipientId,
      });
      const currentUserNotifications = more.filter((notification) =>
        belongsToCurrentUser(notification, currentUserIds),
      );

      setApiNotifications((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        const newOnly = currentUserNotifications.filter((n) => !existingIds.has(n.id));
        return [...prev, ...newOnly];
      });
      archiveNotifications(currentUserNotifications);

      setCurrentPage(nextPage);
      setHasNext(pagination.hasNext);
    } catch {
      // silent fail
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    currentUserIds,
    hasNext,
    isLoadingMore,
    currentPage,
    notificationRecipientId,
    archiveNotifications,
  ]);

  useEffect(() => {
    const currentFeedNotifications = notificationFeed.filter((notification) =>
      belongsToCurrentUser(notification, currentUserIds),
    );
    archiveNotifications(currentFeedNotifications);
  }, [archiveNotifications, currentUserIds, notificationFeed]);

  const allNotifications = useMemo(() => {
    const currentArchivedNotifications = archivedNotifications.filter(
      (notification) => belongsToCurrentUser(notification, currentUserIds),
    );
    const currentApiNotifications = apiNotifications.filter((notification) =>
      belongsToCurrentUser(notification, currentUserIds),
    );
    const currentFeedNotifications = notificationFeed.filter((notification) =>
      belongsToCurrentUser(notification, currentUserIds),
    );
    return mergeNotificationLists(
      currentArchivedNotifications,
      currentApiNotifications,
      currentFeedNotifications,
    );
  }, [apiNotifications, archivedNotifications, currentUserIds, notificationFeed]);

  const adaptedNotifications = useMemo(() => {
    return allNotifications
      .map((notification) =>
        adaptNotification(notification, {
          receivedConnectionRequests: receivedConnectionRequestsData,
          following: followingData,
        }),
      )
      .filter((notification) => !isExpiredNotification(notification));
  }, [allNotifications, followingData, receivedConnectionRequestsData]);

  const notifications = useMemo(() => {
    if (activeTab === "unread") {
      return adaptedNotifications.filter((notification) => !notification.isRead);
    }
    if (activeTab === "request") {
      return adaptedNotifications.filter((notification) => notification.isRequest);
    }
    return adaptedNotifications;
  }, [adaptedNotifications, activeTab]);

  const markAsRead = useCallback(
    async (id) => {
      const targetId = normalizeId(id);
      await markNotificationAsRead(id);
      dispatch({ type: MARK_FEED_NOTIFICATION_READ, payload: id });
      setApiNotifications((prev) =>
        prev.map((notification) =>
          normalizeId(notification.id) === targetId
            ? {
                ...notification,
                isRead: true,
                readAt: notification.readAt ?? new Date().toISOString(),
              }
            : notification,
        ),
      );
      updateArchivedNotifications((prev) =>
        prev.map((notification) =>
          normalizeId(notification.id) === targetId
            ? {
                ...notification,
                isRead: true,
                readAt: notification.readAt ?? new Date().toISOString(),
              }
            : notification,
        ),
      );
    },
    [dispatch, updateArchivedNotifications],
  );

  const markAllAsRead = useCallback(async () => {
    setIsMarkingAllRead(true);
    try {
      await markAllNotificationsAsRead();
      setApiNotifications((prev) =>
        prev.map((notification) => ({
          ...notification,
          isRead: true,
          readAt: notification.readAt ?? new Date().toISOString(),
        })),
      );
      updateArchivedNotifications((prev) =>
        prev.map((notification) => ({
          ...notification,
          isRead: true,
          readAt: notification.readAt ?? new Date().toISOString(),
        })),
      );
      dispatch({ type: MARK_ALL_FEED_READ });
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    } finally {
      setIsMarkingAllRead(false);
    }
  }, [dispatch, updateArchivedNotifications]);

  const executeAction = useCallback(
    async (action) => {
      if (!action?.url && !action?.networkAction) return;

      try {
        if (action.networkAction === "follow-back") {
          const response = await sendFollowRequest(
            notificationRecipientId,
            action.targetUserId,
          );
          if (response?.message && !isAlreadyFollowingMessage(response.message)) {
            throw new Error(response.message);
          }
          notify("Followed back.", { variant: "success", timer: 2500 });
          await refetchFollowing?.();
          await fetchFromApi();
          return;
        }

        if (action.networkAction === "unfollow") {
          const response = await removeFollowRequest(action.targetUserId, {
            followerId: notificationRecipientId,
            clearState: true,
            ignoreMissing: true,
          });
          if (response?.message) throw new Error(response.message);
          notify("Unfollowed.", { variant: "success", timer: 2500 });
          await refetchFollowing?.();
          await fetchFromApi();
          return;
        }

        const token = await getToken();
        const hasBody = action.body !== undefined;
        await axios.request({
          baseURL: baseURL || undefined,
          url: action.url,
          method: action.method || "POST",
          headers: {
            ...(token ? { Authorization: token } : {}),
            ...(hasBody ? { "Content-Type": "application/json" } : {}),
          },
          ...(hasBody ? { data: action.body } : {}),
        });

        let successMessage = "Notification updated.";
        if (action.networkAction === "accept-connection") {
          const followSync = await syncConnectionRequestFollow(
            notificationRecipientId,
            action.targetUserId,
            null,
            { forceAutoFollow: true },
          );

          successMessage = "Connection accepted.";
          if (
            followSync?.message &&
            !isAlreadyFollowingMessage(followSync.message)
          ) {
            notify(
              "Connection accepted, but follow state could not be updated.",
              { variant: "warning", timer: 3500 },
            );
          }
        }

        notify(successMessage, { variant: "success", timer: 2500 });
        try {
          await Promise.all([
            ...(action.networkAction === "accept-connection"
              ? [
                  queryClient.invalidateQueries(["get-connections"]),
                  queryClient.invalidateQueries(["get-connection-requests"]),
                  queryClient.invalidateQueries(["get-followers"]),
                ]
              : []),
            refetchRequests?.(),
            refetchFollowing?.(),
            fetchFromApi(),
          ]);
        } catch (refreshError) {
          console.error(
            "Notification action succeeded, but refresh failed:",
            refreshError,
          );
        }
      } catch (error) {
        console.error("Failed to execute notification action:", error);
        notify(getActionErrorMessage(error), {
          variant: "error",
          timer: 3500,
        });
        throw error;
      }
    },
    [
      fetchFromApi,
      notificationRecipientId,
      notify,
      queryClient,
      refetchFollowing,
      refetchRequests,
    ],
  );

  const unreadCount = useMemo(() => {
    return adaptedNotifications.filter((n) => !n.isRead).length;
  }, [adaptedNotifications]);

  return {
    notifications,
    hasAnyNotifications: adaptedNotifications.length > 0,
    unreadCount,
    isLoading,
    isLoadingMore,
    isMarkingAllRead,
    hasNext,
    error,
    activeTab,
    setActiveTab,
    markAsRead,
    markAllAsRead,
    executeAction,
    retry: fetchFromApi,
    loadMore,
  };
}
